import { useState, useEffect } from "react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ExternalLink, DollarSign, MapPin, Calendar, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

interface Franchise {
  id: string;
  franchise_name: string;
  brand_logo: string | null;
  description: string | null;
  teaser: string | null;
  sector: string;
  franchise_fee: number | null;
  initial_investment_min: number | null;
  initial_investment_max: number | null;
  established_year: number | null;
  total_locations: number | null;
  images: string[] | null;
  featured: boolean;
}

const Franchises = () => {
  const [franchises, setFranchises] = useState<Franchise[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sectorFilter, setSectorFilter] = useState<string>("all");

  useEffect(() => {
    fetchFranchises();
  }, []);

  const fetchFranchises = async () => {
    try {
      const { data, error } = await supabase
        .from("franchises")
        .select("*")
        .eq("status", "live")
        .order("featured", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      setFranchises(data || []);
    } catch (error) {
      console.error("Error fetching franchises:", error);
      toast.error("Failed to load franchises");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (!amount) return "Contact for details";
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getFranchiseImage = (franchise: Franchise) => {
    if (franchise.brand_logo) return franchise.brand_logo;
    if (franchise.images && franchise.images.length > 0) return franchise.images[0];
    return "/placeholder.svg";
  };

  const filteredFranchises = franchises.filter((franchise) => {
    const matchesSearch = franchise.franchise_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      franchise.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSector = sectorFilter === "all" || franchise.sector === sectorFilter;
    return matchesSearch && matchesSector;
  });

  const sectors = Array.from(new Set(franchises.map(f => f.sector)));

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Franchise Opportunities</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Discover proven business models with established brands. Browse our curated selection of franchise opportunities across Australia.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search franchises..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={sectorFilter} onValueChange={setSectorFilter}>
            <SelectTrigger className="w-full md:w-[200px]">
              <SelectValue placeholder="All Sectors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sectors</SelectItem>
              {sectors.map((sector) => (
                <SelectItem key={sector} value={sector}>
                  {sector}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-sm text-muted-foreground">
            Showing {filteredFranchises.length} franchise{filteredFranchises.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Franchises Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading franchises...</p>
          </div>
        ) : filteredFranchises.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No franchises found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFranchises.map((franchise) => (
              <Card key={franchise.id} className="h-full flex flex-col hover:shadow-lg transition-shadow">
                <div className="relative">
                  <img
                    src={getFranchiseImage(franchise)}
                    alt={franchise.franchise_name}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.currentTarget.src = "/placeholder.svg";
                    }}
                  />
                  {franchise.featured && (
                    <Badge className="absolute top-2 right-2" variant="default">
                      Featured
                    </Badge>
                  )}
                </div>
                
                <CardContent className="flex-1 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="secondary">{franchise.sector}</Badge>
                  </div>
                  
                  <h3 className="text-xl font-semibold mb-2">{franchise.franchise_name}</h3>
                  <p className="text-muted-foreground text-sm line-clamp-3 mb-4">
                    {franchise.teaser || franchise.description || "No description available"}
                  </p>

                  <div className="space-y-2 text-sm">
                    {franchise.franchise_fee && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <DollarSign className="h-4 w-4" />
                        <span>Franchise Fee: {formatCurrency(franchise.franchise_fee)}</span>
                      </div>
                    )}
                    
                    {(franchise.initial_investment_min || franchise.initial_investment_max) && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <DollarSign className="h-4 w-4" />
                        <span>
                          Investment: {formatCurrency(franchise.initial_investment_min)} - {formatCurrency(franchise.initial_investment_max)}
                        </span>
                      </div>
                    )}

                    {franchise.total_locations && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span>{franchise.total_locations} locations</span>
                      </div>
                    )}

                    {franchise.established_year && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>Established {franchise.established_year}</span>
                      </div>
                    )}
                  </div>
                </CardContent>

                <CardFooter className="p-6 pt-0">
                  <Button className="w-full" asChild>
                    <Link to={`/franchise/${franchise.id}`}>
                      View Details
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}

        {/* Info Section */}
        <div className="mt-16 bg-muted/30 rounded-lg p-8">
          <h2 className="text-2xl font-bold mb-4">Why Choose a Franchise?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold mb-2">Proven Business Model</h3>
              <p className="text-sm text-muted-foreground">
                Benefit from an established system with a track record of success.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Brand Recognition</h3>
              <p className="text-sm text-muted-foreground">
                Start with an already recognized brand and customer base.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Training & Support</h3>
              <p className="text-sm text-muted-foreground">
                Receive ongoing training and operational support from the franchisor.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Franchises;
